"use client";

import { useMemo } from "react";

import type { QRProps } from "@/contexts/QREditorContext/types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import type { UseBusinessDataResult, BusinessTemplateTranslations } from "../types";
import { buildAddressData, hasBusinessContactInfo, hasEnabledBusinessHours } from "../utils";

/**
 * Resolves business preview data from props with QREditorContext fallback,
 * address formatting, and derived visibility flags for conditional sections.
 */
export const useBusinessData = (props: QRProps<"business">): UseBusinessDataResult => {
  const { data, isInitPreview: isInitialPreview } = usePreviewData(props);

  const translations = props.previewTranslations?.["business"] as BusinessTemplateTranslations;

  const addressData = useMemo(() => buildAddressData(data), [data]);

  const hasOpeningHours = useMemo(
    () => hasEnabledBusinessHours(data?.businessHours),
    [data?.businessHours]
  );

  const hasContactInfo = useMemo(() => hasBusinessContactInfo(data), [data]);

  return {
    translations,
    isInitialPreview,
    data,
    addressData,
    hasOpeningHours,
    hasContactInfo,
  };
};
